/**
 * 04/07 — Fiche merchandising PDF (A4 paysage) à afficher en réserve :
 * planogramme (grille facings colorée), plan du magasin (chaleur + zones),
 * scores et conseils du mois. jsPDF, même style que les autres PDF SCD.
 */
import { jsPDF } from 'jspdf';
import { getSecteur } from './retail';
import {
  NIVEAUX, PARCOURS, TYPES_ZONE, nbColonnes, slotKey, computeFacing, computeParcours, famillesActives,
  merchSecteur, typeMagasinSecteur, typeCelluleFixe, cellulesVides,
} from './merchandising';
import { getTypeMagasin } from './ouverture';

const HEX_FAMILLE = ['#0ea5e9', '#f43f5e', '#f59e0b', '#10b981', '#14b8a6'];
const HEX_ZONE = { promo: '#dc2626', impulsion: '#ec4899', service: '#64748b' };
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

const hex = (v, fams) => {
  if (!v) return '#E5E7EB';
  if (HEX_ZONE[v]) return HEX_ZONE[v];
  const i = fams.findIndex(f => f.id === v);
  return i >= 0 ? HEX_FAMILLE[i % HEX_FAMILLE.length] : '#9CA3AF';
};
const libelle = (v, fams) => {
  if (!v) return 'vide';
  const z = TYPES_ZONE.find(t => t.id === v);
  if (z) return z.nom;
  return fams.find(f => f.id === v)?.nom || v;
};
const chaleurHex = (h) => {
  const t = Math.min(1, Math.max(0, (h - 0.4) / 0.6));
  const a = [191, 219, 254];
  const b = [253, 186, 116];
  return `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * t)).join(',')})`;
};

export async function exportPlanogrammePdf({ retail, secteurId = retail?.secteur, month = 1, companyName = '', output = 'save' }) {
  const sec = getSecteur(secteurId);
  const fams = famillesActives(retail, secteurId);
  const cfg = merchSecteur(retail, secteurId);
  const slots = cfg.facing?.slots || {};
  const cells = cfg.parcours?.cells || cellulesVides();
  const typeId = typeMagasinSecteur(retail, secteurId);
  const cols = nbColonnes(typeId);
  const fx = computeFacing(retail, slots, { secteurId, month });
  const px = computeParcours(retail, cells, { secteurId, month });

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const W = 297;
  const M = 12;
  doc.setFillColor('#7C2D12');
  doc.rect(0, 0, W, 22, 'F');
  doc.setTextColor('#FFFFFF');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(`Fiche merchandising — ${sec?.nom || secteurId}`, M, 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`${companyName || 'SCD'} · ${getTypeMagasin(typeId)?.nom || 'Boutique'} · demande de ${MOIS[(month - 1) % 12]} · à afficher en réserve — s'applique à tous les magasins ${sec?.nom || ''}`, M, 17);

  // ── Planogramme (gauche) ──
  const gx = M;
  let y = 30;
  doc.setTextColor('#7C2D12');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(`PLANOGRAMME — score ${fx.score ?? '—'}/100`, gx, y);
  y += 4;
  const labelW = 30;
  const gridW = 150 - labelW;
  const cw = gridW / cols;
  const rh = 13;
  NIVEAUX.forEach((n, li) => {
    const yy = y + li * rh;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor('#1F2937');
    doc.text(n.nom, gx, yy + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor('#6B7280');
    doc.text(`visibilité x${n.coef}`, gx, yy + 9.5);
    for (let c = 0; c < cols; c++) {
      const v = slots[slotKey(n.id, c)] || null;
      const x = gx + labelW + c * cw;
      doc.setFillColor(hex(v, fams));
      doc.setDrawColor('#FFFFFF');
      doc.rect(x, yy, cw - 0.8, rh - 1, 'FD');
      doc.setTextColor(v ? '#FFFFFF' : '#9CA3AF');
      doc.setFontSize(5.8);
      const lines = doc.splitTextToSize(libelle(v, fams), cw - 2.5);
      doc.text(lines.slice(0, 2), x + 1.2, yy + 5);
    }
    doc.setDrawColor('#9CA3AF');
    doc.setLineWidth(0.8);
    doc.line(gx + labelW, yy + rh - 1, gx + labelW + gridW, yy + rh - 1);
    doc.setLineWidth(0.2);
  });
  y += NIVEAUX.length * rh + 4;
  doc.setFontSize(7.5);
  doc.setTextColor('#1F2937');
  doc.setFont('helvetica', 'bold');
  doc.text('Famille', gx, y); doc.text('Facings', gx + 60, y); doc.text('Visibilité', gx + 78, y); doc.text('Demande', gx + 98, y); doc.text('Effet ventes', gx + 118, y);
  doc.setFont('helvetica', 'normal');
  (fx.details || []).forEach((d) => {
    y += 4.2;
    doc.setFillColor(hex(d.famId, fams));
    doc.rect(gx, y - 2.6, 2.6, 2.6, 'F');
    doc.text(d.nom, gx + 4, y);
    doc.text(String(d.facings), gx + 60, y);
    doc.text(`${Math.round(d.visibShare * 100)} %`, gx + 78, y);
    doc.text(`${Math.round(d.demandeShare * 100)} %`, gx + 98, y);
    doc.text(`x${(fx.familleMult[d.famId] ?? 1).toFixed(2)}`, gx + 118, y);
  });

  // ── Plan du magasin (droite) ──
  const px0 = M + 158;
  let py = 30;
  doc.setTextColor('#7C2D12');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(`PARCOURS CLIENT — score ${px.score ?? '—'}/100`, px0, py);
  py += 4;
  const cellW = 118 / PARCOURS.colonnes;
  const cellH = 17;
  cells.forEach((cell, i) => {
    const l = Math.floor(i / PARCOURS.colonnes);
    const c = i % PARCOURS.colonnes;
    const x = px0 + c * cellW;
    const yy = py + l * cellH;
    const fixe = typeCelluleFixe(i);
    doc.setFillColor(fixe ? '#D1D5DB' : chaleurHex(px.chaleur?.[i] ?? 0.5));
    doc.setDrawColor('#FFFFFF');
    doc.rect(x, yy, cellW - 0.8, cellH - 0.8, 'FD');
    doc.setFontSize(6);
    doc.setTextColor('#374151');
    if (!fixe) doc.text(`${Math.round((px.chaleur?.[i] ?? 0.5) * 100)} %`, x + cellW - 8, yy + 3.5);
    const v = fixe || (cell ? (cell.type === 'famille' ? cell.famId : cell.type) : null);
    const label = fixe ? (fixe === 'entree' ? 'ENTRÉE' : 'CAISSES') : libelle(v, fams);
    if (v && !fixe) {
      doc.setFillColor(hex(v, fams));
      doc.roundedRect(x + 2, yy + 6, cellW - 4.8, 6, 1, 1, 'F');
      doc.setTextColor('#FFFFFF');
    } else doc.setTextColor('#374151');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.2);
    doc.text(doc.splitTextToSize(label, cellW - 6)[0] || '', x + 3, yy + 10);
    if (v === px.bestId) { doc.setTextColor('#B45309'); doc.setFontSize(5.5); doc.text("produit d'appel", x + 3, yy + 14.5); }
  });
  py += PARCOURS.lignes * cellH + 3;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor('#6B7280');
  doc.text('Ligne du haut = fond du magasin (zones froides) · ligne du bas = façade, entrée et caisses (zones chaudes).', px0, py);
  py += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor('#1F2937');
  doc.text(`Effets quotidiens : trafic ${px.traficPct > 0 ? '+' : ''}${px.traficPct} % · conversion ${(fx.conversionPct + px.conversionPct) > 0 ? '+' : ''}${Math.round((fx.conversionPct + px.conversionPct) * 10) / 10} pt · panier ${(fx.panierPct + px.panierPct) > 0 ? '+' : ''}${Math.round((fx.panierPct + px.panierPct) * 10) / 10} %`, px0, py);
  py += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  [...(fx.conseils || []), ...(px.conseils || [])].slice(0, 9).forEach((cns) => {
    const lines = doc.splitTextToSize(`${cns.tone === 'good' ? '✓' : cns.tone === 'bad' ? '✕' : '!'} ${cns.texte}`, 120);
    if (py + lines.length * 3.6 > 200) return;
    doc.setTextColor(cns.tone === 'good' ? '#059669' : cns.tone === 'bad' ? '#B91C1C' : '#B45309');
    doc.text(lines, px0, py);
    py += lines.length * 3.6 + 1;
  });

  doc.setDrawColor('#F3E8E2');
  doc.line(M, 200, W - M, 200);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor('#6B7280');
  doc.text(`SCD — Société de Commerce et de Distribution · Fiche merchandising générée le ${new Date().toLocaleDateString('fr-FR')} · à mettre à jour avant chaque pic saisonnier`, M, 205);

  if (output === 'base64') return doc.output('datauristring').split(',')[1];
  const safe = `${sec?.nom || secteurId}`.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  doc.save(`fiche-merchandising-${safe}.pdf`);
  return null;
}
